import '../styles/globals.css';
import { ClerkProvider, RedirectToSignIn, useAuth } from '@clerk/clerk-react';

import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { HttpLink } from '@apollo/client/link/http';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { getCurrentAuthRedirectUrl } from 'src/client/utility/auth-redirects';

const ApolloWrapper = ({ children }: { children: JSX.Element }) => {
  const { getToken } = useAuth();

  const authLink = setContext(async (_, { headers }) => {
    const token = await getToken();
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_API_URL,
    credentials: 'include',
  });
  const apolloClient = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
  });
  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const publicRoutes = ['/sign-in', '/sign-up'];

const AppContent = ({ Component, pageProps }: AppProps) => {
  const { pathname } = useRouter();
  const { isLoaded, isSignedIn } = useAuth();
  const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route));

  if (isPublicRoute) {
    return <Component {...pageProps} />;
  }

  if (!isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    const redirectUrl = typeof window === 'undefined' ? '/' : getCurrentAuthRedirectUrl(window.location);
    return <RedirectToSignIn redirectUrl={redirectUrl} />;
  }

  return (
    <ApolloWrapper>
      <Component {...pageProps} />
    </ApolloWrapper>
  );
};

const App = (props: AppProps) => (
  <ClerkProvider
    publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY!}
    signInUrl={process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL}
    signUpUrl={process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL}
    signInFallbackRedirectUrl={process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL}
    signUpFallbackRedirectUrl={process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL}
    {...props.pageProps}
  >
    <AppContent {...props} />
  </ClerkProvider>
);

export default App;
